import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import { getRandomDefaultEmoji } from "@/components/emoji/emoji-colors.ts";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import i18n from "@/i18n.ts";
import { useBlogChannelDelete, useBlogChannelSave } from "@/queries/blog.query.ts";
import { useSlackChannels } from "@/queries/integrations/use-integrations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiBlogChannel, getBlogChannelRuleGroups } from "@/types/blog.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  ConfirmationModal,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  Select,
  Stack,
  Switch,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

export interface BlogChannelEditModalProps extends ModalProps {
  channel?: ApiBlogChannel;
}

const buildBlogChannelSchema = () =>
  z
    .object({
      title: z.string().min(1, i18n.t("errors:field_required")),
      emoji: z.string().min(1, i18n.t("errors:field_required")),
      description: z.string().nullish(),
      is_subscribe_all_employees: z.boolean(),
      view_apply_to_groups: ruleGroupsSchema(),
      publish_apply_to_groups: ruleGroupsSchema().refine(
        (groups) => groups.some((group) => group.rules.length > 0),
        i18n.t("errors:field_required")
      ),
      slack_channel_id: z.string().nullish(),
    })
    .superRefine((data, context) => {
      if (
        !data.is_subscribe_all_employees &&
        !data.view_apply_to_groups.some((group) => group.rules.length > 0)
      ) {
        context.addIssue({
          code: "custom",
          path: ["view_apply_to_groups"],
          message: i18n.t("errors:field_required"),
        });
      }
    });

export function BlogChannelEditModal({ channel, ...props }: BlogChannelEditModalProps) {
  const { t } = useTranslation();
  const viewConditionTypes = useRuleConditionTypes(RuleRegistryPlace.blogChannelView);
  const publishConditionTypes = useRuleConditionTypes(RuleRegistryPlace.blogChannelPublish);
  const { id } = props;
  const { canIEdit } = usePermissions();
  const { close, openModal } = useModals();

  const navigate = useNavigate();
  const { data: slackChannels } = useSlackChannels();
  const randomEmoji = useMemo(() => {
    return getRandomDefaultEmoji(emojiDefaults.blog);
  }, []);
  const { mutateAsync: deleteChannel } = useBlogChannelDelete();

  const blogChannelSchema = buildBlogChannelSchema();

  const {
    setValue,
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(blogChannelSchema),
    values: {
      title: channel?.title ?? "",
      description: channel?.description ?? "",
      emoji: channel?.emoji ?? randomEmoji,
      is_subscribe_all_employees: channel?.is_subscribe_all_employees ?? false,
      view_apply_to_groups: getBlogChannelRuleGroups(channel, "view_apply_to").map((group) => ({
        rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
      })),
      publish_apply_to_groups: getBlogChannelRuleGroups(channel, "publish_apply_to").map(
        (group) => ({
          rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
        })
      ),
      slack_channel_id: channel?.slack_channel_id ?? undefined,
    },
  });
  const { isPending, mutateAsync } = useBlogChannelSave(control);
  const isSubscribeAllEmployees = watch("is_subscribe_all_employees");

  const onSubmit = handleSubmit(async (data) => {
    const channelSaved = await mutateAsync({
      id: channel?.id,
      payload: {
        title: data.title,
        description: data.description ?? "",
        emoji: data.emoji,
        view_apply_to_groups: data.is_subscribe_all_employees
          ? []
          : data.view_apply_to_groups.filter((group) => group.rules.length > 0),
        publish_apply_to_groups: data.publish_apply_to_groups.filter(
          (group) => group.rules.length > 0
        ),
        slack_channel_id: data.slack_channel_id,
        is_subscribe_all_employees: data.is_subscribe_all_employees,
      },
    });

    analyticsService.trackEvent(
      channel?.id ? analyticEvents.blog.channelUpdated : analyticEvents.blog.channelCreated,
      {
        [analyticProperties.id]: channelSaved.id,
      }
    );

    if (channelSaved) {
      navigate(`/news/${channelSaved.id}`);
      close(id);
    }
  });

  const handleDeleteChannel = async () => {
    if (!channel) return;
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("blog:delete_channel_question"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        const result = await deleteChannel(channel.id);

        if (result?.success) {
          analyticsService.trackEvent(analyticEvents.blog.channelDeleted);
          close(id);
          navigate(`/news`);
        }
      },
    });
  };

  return (
    <Modal
      title={channel ? t("blog:edit_channel") : t("blog:create_channel")}
      withCloser={true}
      size={"lg"}
      actions={
        <Stack gap={"md"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} variant={"primary"} onClick={onSubmit} isLoading={isPending}>
            {channel ? t("common:save") : t("common:create_new")}
          </Button>
          <Button
            size={"md"}
            variant={"plain"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
      {...props}
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Stack items={"center"}>
            <Controller
              control={control}
              name={"emoji"}
              render={({ field: { value, onChange } }) => (
                <EmojiPickerDropdown
                  onEmojiSelect={(em) => {
                    onChange(em);
                  }}
                >
                  <Box
                    className={
                      "relative w-41 h-41 group cursor-pointer rounded-md bg-brand-dark/5 shadow-lg"
                    }
                  >
                    {value && (
                      <Avatar variant={"plain"} url={undefined} emoji={value} size={"2xl"} />
                    )}
                    <Box
                      className={
                        "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-md flex items-center justify-center"
                      }
                    >
                      <FontAwesomeIcon
                        icon={"fa-light fa-icons"}
                        className={"text-[74px] text-light "}
                      />
                    </Box>
                  </Box>
                </EmojiPickerDropdown>
              )}
            />

            {errors.emoji && (
              <Box className="pt-2 pb-4">
                <span className="text-danger">{errors.emoji.message}</span>
              </Box>
            )}
          </Stack>
          <Stack>
            <Input
              label={t("blog:feed_name")}
              {...register("title")}
              type={"text"}
              error={errors.title?.message}
            ></Input>
            <Textarea
              label={t("common:description")}
              {...register("description")}
              error={errors.description?.message}
            ></Textarea>
          </Stack>
          <Controller
            control={control}
            name={"is_subscribe_all_employees"}
            render={({ field }) => (
              <Switch
                title={t("blog:subscribe_all_employees")}
                description={t("blog:subscribe_all_employees_caption")}
                checked={field.value}
                onChange={() => {
                  field.onChange(!field.value);
                }}
              />
            )}
          />
          {!isSubscribeAllEmployees && (
            <Stack>
              <Title
                header={t("blog:visible_to")}
                caption={t("blog:visible_to_caption")}
                paddingTop={true}
                paddingBottom={true}
                size={"md"}
              />

              <RuleGroupsEditor
                control={control}
                fieldName="view_apply_to_groups"
                place={RuleRegistryPlace.blogChannelView}
                setValue={setValue}
                conditionTypes={viewConditionTypes.conditionTypes}
                error={errors.view_apply_to_groups?.message}
              />
            </Stack>
          )}
          <Stack>
            <Title
              header={t("blog:publish_to")}
              caption={t("blog:publish_to_caption")}
              paddingTop={true}
              paddingBottom={true}
              size={"md"}
            />

            <RuleGroupsEditor
              control={control}
              fieldName="publish_apply_to_groups"
              place={RuleRegistryPlace.blogChannelPublish}
              setValue={setValue}
              conditionTypes={publishConditionTypes.conditionTypes}
              error={errors.publish_apply_to_groups?.message}
            />
          </Stack>
          {slackChannels && slackChannels.length > 0 && (
            <Stack>
              <Title
                header={t("integrations:settings_integrations_title")}
                paddingTop={true}
                paddingBottom={true}
                size={"md"}
              />

              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    searchable={true}
                    options={slackChannels?.map((channel) => ({
                      id: channel.channel_id,
                      title: channel.name,
                      icon: channel.is_private ? "fa-light fa-lock" : "fa-brands fa-slack",
                    }))}
                    onChange={onChange}
                    label={t("blog:slack_channel")}
                    emptyTitle={""}
                    isClearable
                  />
                )}
                name={"slack_channel_id"}
              />
            </Stack>
          )}

          {channel && canIEdit(ApiPermission.news_manage_channels) && (
            <DeleteListItem
              onDelete={handleDeleteChannel}
              title={t("blog:delete_channel_question")}
            />
          )}
        </Stack>
      </form>
    </Modal>
  );
}
